import Phaser from 'phaser';
import FRAME_DATA from './spriteFrameData.json';

/**
 * Per-character animation frame data, auto-generated by scripts/buildSprites.cjs.
 * Composite layout per spritesheet: [idle | walk | attack | hurt | death]
 *
 * All sprites face RIGHT by default.
 * Heroes are rendered as-is (face right).
 * Enemies have setFlipX(true) applied at spawn so they face left (toward the base).
 */
type FrameDataKey = keyof typeof FRAME_DATA;

function getSectionRange(key: FrameDataKey, name: string): { start: number; end: number } | null {
  const entry = (FRAME_DATA as Record<string, { sections: { name: string; start: number; end: number }[] }>)[key];
  const section = entry?.sections.find((s) => s.name === name);
  return section ? { start: section.start, end: section.end } : null;
}

function createAnim(
  scene: Phaser.Scene,
  key: string,
  textureKey: string,
  start: number,
  end: number,
  frameRate: number,
  repeat: number,
): void {
  if (scene.anims.exists(key)) return;
  if (!scene.textures.exists(textureKey)) return;
  const available = scene.textures.get(textureKey).getFrameNames().length;
  if (available === 0 || start >= available) return;
  const safeEnd = Math.min(end, available - 1);
  scene.anims.create({
    key,
    frames: scene.anims.generateFrameNumbers(textureKey, { start, end: safeEnd }),
    frameRate,
    repeat,
  });
}

/**
 * Register all hero, enemy, and NPC animations.
 * Call once from BootScene after assets are loaded.
 *
 * Animation key pattern:
 *   hero_<class>_<state>    e.g. hero_warrior_idle, hero_mage_attack
 *   enemy_<type>_<state>    e.g. enemy_wolf_walk, enemy_dragon_death
 *   npc_mimic_<state>
 */
export function registerAnimations(scene: Phaser.Scene): void {

  // ── Heroes ────────────────────────────────────────────────────────────────
  // Druid composite replaced with the Pixellab male-druid art in commit <this one>;
  // the old Satyr_1 idle override is gone — spriteFrameData.json drives the anim ranges.
  // Shaman (Zhorak) is registered here so animations exist for testing, even though the
  // heroClass isn't playable yet (no balance stats / HeroSelect entry).
  const heroClasses = ['warrior', 'tank', 'wizard', 'archer', 'healer', 'assassin', 'dwarf', 'amazon', 'valkyrie', 'druid', 'shaman', 'necromancer'] as const; // 'dwarf'=Lootfi, 'amazon'=Zayla, 'valkyrie'=Valka, 'necromancer'=Morveth, 'shaman'=Zhorak (sprite-only)

  for (const cls of heroClasses) {
    const tex = `hero_${cls}` as FrameDataKey;
    const idle   = getSectionRange(tex, 'idle');
    const walk   = getSectionRange(tex, 'walk');
    const attack = getSectionRange(tex, 'attack');
    const hurt   = getSectionRange(tex, 'hurt');
    const death  = getSectionRange(tex, 'death');

    if (idle)   createAnim(scene, `${tex}_idle`,   tex, idle.start,   idle.end,    8, -1); // loop
    if (walk)   createAnim(scene, `${tex}_walk`,   tex, walk.start,   walk.end,   10, -1); // loop (used when hero moves)
    if (attack) createAnim(scene, `${tex}_attack`, tex, attack.start, attack.end, 12,  0); // once
    if (hurt)   createAnim(scene, `${tex}_hurt`,   tex, hurt.start,   hurt.end,   10,  0); // once
    if (death)  createAnim(scene, `${tex}_death`,  tex, death.start,  death.end,   8,  0); // once
  }

  // ── Thorne Bear Form (great bear transformation) ──────────────────────────
  // Single 9-frame sheet at 64×64: 0 = idle, 1-4 = walk, 5-8 = attack. Old `_nature_*`
  // texture keys are kept as animation aliases so the BattleScene call sites don't need
  // to change — they just point at frame ranges of the new bear sheet now.
  createAnim(scene, 'hero_druid_nature_idle',   'hero_druid_bear', 0, 0, 8, -1);
  createAnim(scene, 'hero_druid_nature_walk',   'hero_druid_bear', 1, 4, 8, -1);
  createAnim(scene, 'hero_druid_nature_attack', 'hero_druid_bear', 5, 8, 12, 0);
  // Main-menu Rudy idle — Animal_4 Idle, 7 frames, slow loop for ambient animation.
  createAnim(scene, 'vendor_cat_idle', 'vendor_cat_idle', 0, 6, 6, -1);
  // In-battle Rudy (Animal_4 cat) — idle loop + walk loop + special one-shot played on click.
  createAnim(scene, 'whiskers_battle_idle',    'whiskers_battle_idle',    0,  6, 6, -1);
  createAnim(scene, 'whiskers_battle_walk',    'whiskers_battle_walk',    0, 10, 8, -1);
  createAnim(scene, 'whiskers_battle_special', 'whiskers_battle_special', 0,  3, 8,  0);

  // Blacksmith NPC — working anim (12-frame anvil hammer loop, ~9 fps so the hammer
  // swings read as physical impacts) + standing idle (7-frame slow breathing loop
  // played while the player has the dialog open).
  createAnim(scene, 'npc_blacksmith_working', 'npc_blacksmith_working', 0, 11, 9, -1);
  createAnim(scene, 'npc_blacksmith_idle',    'npc_blacksmith_idle',    0,  6, 5, -1);

  // Rune Sage NPC — slow 8-frame idle sway (default), 4-frame channel-pose special
  // (one-shot played on click). Quest-unlocked via "The Sage's Pilgrimage".
  createAnim(scene, 'npc_sage_idle',    'npc_sage_idle',    0, 7, 5, -1);
  createAnim(scene, 'npc_sage_special', 'npc_sage_special', 0, 3, 7,  0);

  // ── Enemies ───────────────────────────────────────────────────────────────
  // ── Ranged enemy shared attack animation — Elf_3 Attack, 12 frames ──
  // Used by skeleton archer (ghoul) and orc scout (goblin) — enemies already setFlipX(true) so faces left naturally
  createAnim(scene, 'enemy_ranged_shoot', 'enemy_ranged_shoot_sheet', 0, 11, 12, 0); // once

  // ── Void Worm (Shadow Realm fast poison worm — Mimic_3) ──
  const wormTex = 'enemy_void_worm' as FrameDataKey;
  const wIdle   = getSectionRange(wormTex, 'idle');
  const wWalk   = getSectionRange(wormTex, 'walk');
  const wAttack = getSectionRange(wormTex, 'attack');
  const wHurt   = getSectionRange(wormTex, 'hurt');
  const wDeath  = getSectionRange(wormTex, 'death');
  if (wIdle)   createAnim(scene, 'enemy_void_worm_idle',   wormTex, wIdle.start,   wIdle.end,   6,  -1);
  if (wWalk)   createAnim(scene, 'enemy_void_worm_walk',   wormTex, wWalk.start,   wWalk.end,   12, -1);
  if (wAttack) createAnim(scene, 'enemy_void_worm_attack', wormTex, wAttack.start, wAttack.end, 12,  0);
  if (wHurt)   createAnim(scene, 'enemy_void_worm_hurt',   wormTex, wHurt.start,   wHurt.end,   10,  0);
  if (wDeath)  createAnim(scene, 'enemy_void_worm_death',  wormTex, wDeath.start,  wDeath.end,   8,  0);

  // ── White Claw dedicated Run animation (9 frames, 0-8) — played as the wolf boss's
  // movement loop in place of the shared walk cycle so his advance reads as a full sprint.
  if (scene.textures.exists('enemy_whitewolf_run')) {
    createAnim(scene, 'enemy_whitewolf_run', 'enemy_whitewolf_run', 0, 8, 14, -1);
  }
  // Black werewolf Run anim — used by every wolf-typed minion/elite so they sprint
  // (instead of trotting on the slower walk cycle) when closing the gap.
  if (scene.textures.exists('enemy_wolf_run')) {
    createAnim(scene, 'enemy_wolf_run', 'enemy_wolf_run', 0, 8, 14, -1);
  }

  const enemyTypes = ['wolf', 'whitewolf', 'skeleton', 'wraith', 'orc', 'orc_elite', 'imp', 'demon', 'dragon', 'telurion', 'satyr', 'minotaur', 'minotaur_ranged', 'minotaur_elite', 'goblin_a', 'goblin_b', 'goblin_elite', 'shadowlord', 'ghost'] as const;

  for (const type of enemyTypes) {
    const tex = `enemy_${type}` as FrameDataKey;
    const idle   = getSectionRange(tex, 'idle');
    const walk   = getSectionRange(tex, 'walk');
    const attack = getSectionRange(tex, 'attack');
    const hurt   = getSectionRange(tex, 'hurt');
    const death  = getSectionRange(tex, 'death');

    if (idle)   createAnim(scene, `${tex}_idle`,   tex, idle.start,   idle.end,    8, -1);
    if (walk)   createAnim(scene, `${tex}_walk`,   tex, walk.start,   walk.end,    8, -1);
    // Skeleton Archer (wraith) fires a 15-frame shot animation — slightly slower for dramatic arrow arc
    const atkFps = type === 'wraith' ? 10 : 12;
    if (attack) createAnim(scene, `${tex}_attack`, tex, attack.start, attack.end, atkFps, 0);
    if (hurt)   createAnim(scene, `${tex}_hurt`,   tex, hurt.start,   hurt.end,   10,  0);
    if (death)  createAnim(scene, `${tex}_death`,  tex, death.start,  death.end,   8,  0);
  }

  // ── Zone Bosses ───────────────────────────────────────────────────────────
  const bossTypes = ['boss_demon1', 'boss_demon2', 'boss_spearman', 'boss_skeleton_mage', 'boss_darkelf', 'boss_gorgon', 'boss_voidelf', 'boss_lava_golem'] as const;
  for (const bossKey of bossTypes) {
    const tex = bossKey as FrameDataKey;
    const idle   = getSectionRange(tex, 'idle');
    const walk   = getSectionRange(tex, 'walk');
    const attack = getSectionRange(tex, 'attack');
    const hurt   = getSectionRange(tex, 'hurt');
    const death  = getSectionRange(tex, 'death');
    // Archivist gets a slower idle (5 fps) — 5-frame standing sway should breathe, not twitch.
    const idleFps = tex === 'boss_skeleton_mage' ? 5 : 8;
    if (idle)   createAnim(scene, `${tex}_idle`,   tex, idle.start,   idle.end,    idleFps, -1);
    if (walk)   createAnim(scene, `${tex}_walk`,   tex, walk.start,   walk.end,    8, -1);
    if (attack) createAnim(scene, `${tex}_attack`, tex, attack.start, attack.end, 10,  0);
    if (hurt)   createAnim(scene, `${tex}_hurt`,   tex, hurt.start,   hurt.end,   10,  0);
    if (death)  createAnim(scene, `${tex}_death`,  tex, death.start,  death.end,   8,  0);
  }

  // ── Rot Bomber (Mimic_2) — event mob, explodes on death ──────────────────
  const rbTex = 'enemy_rotbomber' as FrameDataKey;
  const rbIdle    = getSectionRange(rbTex, 'idle');
  const rbWalk    = getSectionRange(rbTex, 'walk');
  const rbHurt    = getSectionRange(rbTex, 'hurt');
  const rbDeath   = getSectionRange(rbTex, 'death');
  const rbSpecial = getSectionRange(rbTex, 'special');
  if (rbIdle)    createAnim(scene, 'enemy_rotbomber_idle',    rbTex, rbIdle.start,    rbIdle.end,     6, -1);
  if (rbWalk)    createAnim(scene, 'enemy_rotbomber_walk',    rbTex, rbWalk.start,    rbWalk.end,     9, -1);
  if (rbHurt)    createAnim(scene, 'enemy_rotbomber_hurt',    rbTex, rbHurt.start,    rbHurt.end,    10,  0);
  if (rbDeath)   createAnim(scene, 'enemy_rotbomber_death',   rbTex, rbDeath.start,   rbDeath.end,    8,  0);
  // Special plays once at low HP — reveals the true form before exploding. No loop.
  if (rbSpecial) createAnim(scene, 'enemy_rotbomber_special', rbTex, rbSpecial.start, rbSpecial.end,  8,  0);

  // ── Mimic NPC ─────────────────────────────────────────────────────────────
  const mimicTex = 'npc_mimic' as FrameDataKey;
  const mIdle   = getSectionRange(mimicTex, 'idle');
  const mWalk   = getSectionRange(mimicTex, 'walk');
  const mAttack = getSectionRange(mimicTex, 'attack');
  const mHurt   = getSectionRange(mimicTex, 'hurt');
  const mDeath  = getSectionRange(mimicTex, 'death');

  if (mIdle)   createAnim(scene, 'npc_mimic_idle',   mimicTex, mIdle.start,   mIdle.end,    6, -1);
  if (mWalk)   createAnim(scene, 'npc_mimic_walk',   mimicTex, mWalk.start,   mWalk.end,    8, -1);
  if (mAttack) createAnim(scene, 'npc_mimic_attack', mimicTex, mAttack.start, mAttack.end,  10,  0);
  if (mHurt)   createAnim(scene, 'npc_mimic_hurt',   mimicTex, mHurt.start,   mHurt.end,    10,  0);
  if (mDeath)  createAnim(scene, 'npc_mimic_death',  mimicTex, mDeath.start,  mDeath.end,    8,  0);

  // ── Gorgon Queen: split attack into 3 sub-animations (melee1, melee2, spell gaze) ──
  createAnim(scene, 'boss_gorgon_attack1', 'boss_gorgon', 20, 25, 10, 0); // melee swing 1
  createAnim(scene, 'boss_gorgon_attack2', 'boss_gorgon', 26, 31, 10, 0); // melee swing 2
  createAnim(scene, 'boss_gorgon_attack3', 'boss_gorgon', 32, 35, 12, 0); // stone gaze cast

  // ── Gorgon Echo (variant boss): 5-row sheet — idle r0:0-6, walk r1:16-28, attack r2:32-47, special r3:48-52, hurt r4:64-66, death r4:67-69 ──
  createAnim(scene, 'boss_gorgon2_idle',    'boss_gorgon2',  0,  6,  8, -1);
  createAnim(scene, 'boss_gorgon2_walk',    'boss_gorgon2', 16, 28,  8, -1);
  createAnim(scene, 'boss_gorgon2_attack',  'boss_gorgon2', 32, 47, 14,  0);
  createAnim(scene, 'boss_gorgon2_special', 'boss_gorgon2', 48, 52, 10,  0);
  createAnim(scene, 'boss_gorgon2_hurt',    'boss_gorgon2', 64, 66, 10,  0);
  createAnim(scene, 'boss_gorgon2_death',   'boss_gorgon2', 67, 69,  8,  0);

  // ── Lava Golem: charge animation (frames 30-38 from combined sheet) ──
  createAnim(scene, 'boss_lava_golem_charge', 'boss_lava_golem', 30, 38, 12, 0); // once — ground slam rush

  // ── Telurion attack 2 — wider 10-frame fire-breath cast. Idle + Attack_1
  // are auto-created by the generic enemy loop above (sections idle 0-6,
  // attack 7-10). Attack_2 is registered here explicitly because the loop
  // only knows the standard {idle, walk, attack, hurt, death} names.
  // Section attack2 covers frames 11-20 (see spriteFrameData.json). ──
  if (scene.textures.exists('enemy_telurion')) {
    createAnim(scene, 'enemy_telurion_attack2', 'enemy_telurion', 11, 20, 12, 0);
  }

  // ── Shadowlord (stage-50 endboss). Idle / walk / attack / hurt / death
  //    come from the auto-loop on the combined `enemy_shadowlord` texture.
  //    The phase abilities live on dedicated sheets — each one a separate
  //    8-frame 256×256 strip. Anim keys mirror the texture keys for clean
  //    play() calls in BattleScene's phase handler:
  //      attack2 — Phase 1 fire-trap cast (Attack2.png)
  //      attack3 — Phase 3 kill-zone spell (Attack3.png)
  //      special — Phase 2 invuln / summon (Special.png) ──
  if (scene.textures.exists('enemy_shadowlord_attack2')) {
    createAnim(scene, 'enemy_shadowlord_attack2', 'enemy_shadowlord_attack2', 0, 7, 10, 0);
  }
  if (scene.textures.exists('enemy_shadowlord_attack3')) {
    createAnim(scene, 'enemy_shadowlord_attack3', 'enemy_shadowlord_attack3', 0, 7, 10, 0);
  }
  if (scene.textures.exists('enemy_shadowlord_special')) {
    createAnim(scene, 'enemy_shadowlord_special',  'enemy_shadowlord_special', 0, 7, 8, 0);
  }

  // ── Shadowlord killzone telegraph + impact (ree-magic VFX strips).
  //    Telegraph (8f) plays during the windup; impact (6f) on the kill-zone
  //    eruption. Used by VFXManager.playShadowlordKillzone(). ──
  if (scene.textures.exists('effect_shadowlord_killzone')) {
    createAnim(scene, 'fx_shadowlord_killzone', 'effect_shadowlord_killzone', 0, 7, 12, 0);
  }
  if (scene.textures.exists('effect_shadowlord_impact')) {
    createAnim(scene, 'fx_shadowlord_impact',   'effect_shadowlord_impact',   0, 5, 14, 0);
  }
  // Malakhar phase-1 Void Hex — 13-frame stone-shard eruption. Played once
  // at the slot center on trap detonation. 18 fps gives a ~720 ms anim,
  // matching the 4 s DoT window with three full plays.
  if (scene.textures.exists('effect_void_hex')) {
    createAnim(scene, 'fx_void_hex', 'effect_void_hex', 0, 12, 18, 0);
  }

  // ── Archivist (Wanderer Magican): area_attack animation plays during Frost Nova cast ──
  createAnim(scene, 'boss_skeleton_mage_area_attack', 'boss_skeleton_mage_areaatk', 0, 7, 10, 0); // once — 8-frame area cast

  // ── Healer v2 — separate sheet animations ────────────────────────────────────
  createAnim(scene, 'hero_healer2_idle',    'hero_healer2_idle_sheet',    0,  7,  8, -1); // loop idle (8f)
  createAnim(scene, 'hero_healer2_walk',    'hero_healer2_walk_sheet',    0, 11, 10, -1); // loop walk (12f)
  createAnim(scene, 'hero_healer2_attack',  'hero_healer2_attack_sheet',  0, 13, 12,  0); // once attack (14f)
  createAnim(scene, 'hero_healer2_special', 'hero_healer2_special_sheet', 0, 10, 10,  0); // once special cast (11f)

  // ── Cyrith / Sorcerer — pack 2 Lightning Mage (red-robe blonde mage) ─────
  // Pack 2 has no dedicated Idle / Hurt / Dead sheets, so we reuse what's
  // available: Rest.png covers idle (frames 0-1 = subtle breathing) AND
  // death (frames 0-5 = stand → sit → collapse). Hurt is a single attack
  // frame (the BattleScene applies a red tint instead of a real anim).
  if (scene.textures.exists('hero_sorcerer')) {
    // Idle uses Rest sheet frames 0-1 only — a slow 2-frame breathing loop
    // that holds the standing pose without animating into the sit-down.
    createAnim(scene, 'hero_sorcerer_idle',   'hero_sorcerer',              0, 1,  3, -1);
    // Death anim plays the full Rest.png (stand → lean → sit → collapse).
    createAnim(scene, 'hero_sorcerer_death',  'hero_sorcerer',              0, 5,  6,  0);
  }
  if (scene.textures.exists('hero_sorcerer_walk_sheet')) {
    createAnim(scene, 'hero_sorcerer_walk',   'hero_sorcerer_walk_sheet',   0, 6, 12, -1); // 7-frame Roll sheet repurposed as walk
  }
  if (scene.textures.exists('hero_sorcerer_attack_sheet')) {
    // Auto-attack variant 1 — Pack 2 Attack_1 (4f, single-cast pose).
    // Aliased as `hero_sorcerer_attack` for back-compat with any callers
    // that still reference the canonical key.
    createAnim(scene, 'hero_sorcerer_attack',  'hero_sorcerer_attack_sheet', 0, 3, 10,  0);
    createAnim(scene, 'hero_sorcerer_attack1', 'hero_sorcerer_attack_sheet', 0, 3, 10,  0);
    // Hurt uses the same attack sheet's first frame as a static pose — the
    // hero sprite gets a red tint applied separately for damage feedback.
    createAnim(scene, 'hero_sorcerer_hurt',    'hero_sorcerer_attack_sheet', 0, 0,  6,  0);
  }
  if (scene.textures.exists('hero_sorcerer_attack2_sheet')) {
    // Auto-attack variant 2 (Pack 2 Attack_2). The same sheet still drives
    // Storm Bolt's cast pose — separate anim keys keep the auto-swing and
    // spell channel independent in case one needs different timing later.
    createAnim(scene, 'hero_sorcerer_attack2',    'hero_sorcerer_attack2_sheet', 0, 3, 10, 0);
    createAnim(scene, 'hero_sorcerer_storm_bolt', 'hero_sorcerer_attack2_sheet', 0, 3, 12, 0);
  }
  if (scene.textures.exists('hero_sorcerer_attack3_sheet')) {
    // Auto-attack variant 3 — Pack 2 Attack_3 (4f). Cyrith rotates 1→2→3
    // on auto-attacks so the hero never repeats the same pose twice in a
    // row, breaking the static-cast-loop look.
    createAnim(scene, 'hero_sorcerer_attack3', 'hero_sorcerer_attack3_sheet', 0, 3, 10, 0);
  }
  if (scene.textures.exists('hero_sorcerer_area_sheet')) {
    // Area Attack — full 12-frame channel + lightning eruption. Used by
    // Arc Strike per user request (entire anim plays during the cast).
    createAnim(scene, 'hero_sorcerer_area_attack', 'hero_sorcerer_area_sheet', 0, 11, 14, 0);
  }

  // ── Warrior special charge/alt animations (separate spritesheets) ────────────
  createAnim(scene, 'hero_warrior_run_anim',     'hero_warrior_run_sheet',     0, 7, 14, -1); // looping run (charge approach/return)
  createAnim(scene, 'hero_warrior_charge_anim',  'hero_warrior_charge_sheet',  0, 4, 10,  0); // once — landing strike

  // ── Amazon (Zayla) special animations (separate spritesheets) ────────────
  createAnim(scene, 'hero_amazon_run_anim',     'hero_amazon_run_sheet',     0,  9, 12, -1); // looping run (10 frames)
  createAnim(scene, 'hero_amazon_jump_anim',    'hero_amazon_jump_sheet',    0, 10,  7,  0); // once — leap charge (11 frames)
  // Special anim is now ONE-SHOT (was looping). Each ranged auto-attack plays it
  // once and the hero returns to her real Idle pose, so Idle.png and Idle_2.png
  // become the resting state instead of an endless throw loop.
  createAnim(scene, 'hero_amazon_special_anim', 'hero_amazon_special_sheet', 0,  8, 12,  0); // once — ranged auto-attack throw (9 frames)
  createAnim(scene, 'hero_amazon_attack2_anim', 'hero_amazon_attack2_sheet', 0,  4, 12,  0); // once — melee combo strike (5 frames)
  createAnim(scene, 'hero_amazon_spear_anim',   'hero_amazon_spear_sheet',   0,  8, 14,  0); // once — javelin throw (9 frames, 64px)
  // Idle_2 — 9-frame breathing-variation loop. Zayla picks between hero_amazon_idle
  // (composite frames 0-6, the standard pose) and this one on a soft 80/20 schedule
  // so her resting state isn't a single repeating loop.
  createAnim(scene, 'hero_amazon_idle2',        'hero_amazon_idle2_sheet',   0,  8,  6, -1); // looping

  // ── Dwarf special animations (separate spritesheets) ─────────────────────
  createAnim(scene, 'hero_dwarf_run_anim',    'hero_dwarf_run_sheet',     0,  9, 12, -1); // looping run (10 frames)
  createAnim(scene, 'hero_dwarf_charge_anim', 'hero_dwarf_charge_sheet',  0,  6,  9,  0); // once — landing strike (7 frames)
  createAnim(scene, 'hero_dwarf_special_anim','hero_dwarf_special_sheet',  0,  7, 10,  0); // once — mace throw (8 frames)

  // ── Valka (Valkyrie) special animations (separate spritesheets) ──────────
  // Run sheet used for Shield Bash charge; Attack_2 sheet is the Bludgeon Slam
  // finisher (3-frame mace swing); Special sheet plays on banner plant; Jump
  // is reserved if we ever wire a leap opener to her kit.
  createAnim(scene, 'hero_valkyrie_run_anim',     'hero_valkyrie_run_sheet',      0,  9, 12, -1); // looping run (10 frames)
  createAnim(scene, 'hero_valkyrie_attack2_anim', 'hero_valkyrie_attack2_sheet',  0,  2, 12,  0); // once — bludgeon slam (3 frames)
  createAnim(scene, 'hero_valkyrie_special_anim', 'hero_valkyrie_special_sheet',  0,  4, 10,  0); // once — banner plant (5 frames)
  createAnim(scene, 'hero_valkyrie_jump_anim',    'hero_valkyrie_jump_sheet',     0, 10, 10,  0); // once — leap (11 frames)

  // ── Valka ability VFX strips (from Combat FX 1.1 pack) ──────────────────
  createAnim(scene, 'fx_shieldbash', 'effect_fx_shieldbash', 0, 5, 15, 0); // once — shield bash impact (6 frames)
  createAnim(scene, 'fx_bludgeon',   'effect_fx_bludgeon',   0, 7, 14, 0); // once — Tempest Slam finisher (8 frames, Combat FX Preview 16)
  createAnim(scene, 'fx_whirlwind',  'effect_fx_whirlwind',  0, 5, 14, 0); // once — whirlwind arc  (6 frames, Combat FX Preview 1)
  // Valka Banner of Valor — yellow waving pennant. Plays for the entire
  // banner lifetime as a looping animation (repeat: -1).
  createAnim(scene, 'fx_banner_flag', 'effect_banner_flag', 0, 5, 8, -1);

  // ── Necromancer (Morveth) — all anims come from the 39-frame composite via
  // the heroClasses auto-loop above. Spell casts (Death Coil / Raise Dead /
  // Bone Shield) replay the standard attack anim so everything stays on the
  // new side-view sprite instead of the old per-cast sheets.

  // ── Copper Golem — Sorcerer's hero summon ─────────────────────────────────
  // Stationary combatant (occupies a slot, no walk/run cycle). 4 anims from
  // 4 separate sheets — keys are namespaced `summon_copper_golem_*` to keep
  // them distinct from any future class hero textures.
  if (scene.textures.exists('summon_copper_golem_idle')) {
    createAnim(scene, 'summon_copper_golem_idle_anim',   'summon_copper_golem_idle',   0, 5, 6, -1); // 6 frames, looping
  }
  if (scene.textures.exists('summon_copper_golem_attack')) {
    createAnim(scene, 'summon_copper_golem_attack_anim', 'summon_copper_golem_attack', 0, 5, 10, 0); // 6 frames, once
  }
  if (scene.textures.exists('summon_copper_golem_attack2')) {
    // Heavy chain-slam — 7 frames, slightly slower so the wind-up reads.
    createAnim(scene, 'summon_copper_golem_attack2_anim', 'summon_copper_golem_attack2', 0, 6, 8, 0); // 7 frames, once
  }
  if (scene.textures.exists('summon_copper_golem_hurt')) {
    createAnim(scene, 'summon_copper_golem_hurt_anim',   'summon_copper_golem_hurt',   0, 3, 12, 0); // 4 frames, once
  }
  if (scene.textures.exists('summon_copper_golem_dead')) {
    createAnim(scene, 'summon_copper_golem_dead_anim',   'summon_copper_golem_dead',   0, 5, 8, 0);  // 6 frames, once
  }

  // ── Shaman totem idle sway ─────────────────────────────────────────────────
  if (scene.textures.exists('totem_shaman')) {
    createAnim(scene, 'totem_shaman_idle', 'totem_shaman', 0, 8, 6, -1); // looping
  }

  // ── Lightning bolt strike VFX ─────────────────────────────────────────────
  createAnim(scene, 'fx_lightning_bolt_strike', 'effect_lightning_bolt_strike', 0, 9, 16, 0);

  // ── Merchant NPC — Trader_1: idle(0-5) + approval(6-13) ──────────────────
  // Trader_1 split sheet: idle(0-5) | idle2(6-16) | idle3(17-23) | dialogue(24-39) | approval(40-47)
  createAnim(scene, 'npc_merchant_idle',     'npc_merchant',  0,  5, 8, -1); // idle 1 (loop)
  createAnim(scene, 'npc_merchant_idle2',    'npc_merchant',  6, 16, 8,  0); // idle 2 (once)
  createAnim(scene, 'npc_merchant_idle3',    'npc_merchant', 17, 23, 8,  0); // idle 3 (once)
  createAnim(scene, 'npc_merchant_dialogue', 'npc_merchant', 24, 39, 8,  0); // dialogue (once)
  createAnim(scene, 'npc_merchant_approval', 'npc_merchant', 40, 47, 8,  0); // approval (once)

  // ── VFX effects ───────────────────────────────────────────────────────────
  createAnim(scene, 'fx_mace_throw',     'effect_mace_throw',     0, 9, 14, 0); // Lootfi mace projectile
createAnim(scene, 'frost_anim',        'frost_effect',          0, 7, 12, 0);
  createAnim(scene, 'heal_anim',         'heal_effect',           0, 7, 12, 0);
  createAnim(scene, 'fx_hemorrhage',    'effect_hemorrhage',     0, 3, 10,  0);  // 4f 64×64  — bleed/bleeding status
  createAnim(scene, 'fx_aura_arcane',  'effect_aura_arcane',    0, 59, 18, -1); // 60f 64×128 — Arcane Might buff aura (looping)
  createAnim(scene, 'fx_aura_berserk', 'effect_aura_berserk',   0,  4, 12, -1); // 5f  64×64  — Goomar Berserk aura (looping)
  createAnim(scene, 'fx_aura_bless',     'effect_aura_bless',     0,  5, 10, -1); // 6f  64×64  — Lumiel Bless HoT aura (looping)
  createAnim(scene, 'fx_aura_resurrect', 'effect_aura_resurrect', 0, 59, 18, -1); // 60f 64×128 — resurrection aura (temp, ~3s)
  createAnim(scene, 'fx_aura_shield',    'effect_aura_shield',    0,  6, 10, -1); // 7f 64×64 — Paralyzed sparks shield aura
  createAnim(scene, 'fx_cursed',         'effect_cursed',         0,  4,  8, -1); // 5f 64×64  — Necro minion cursed overlay (looping)
  createAnim(scene, 'fx_petrify',        'effect_petrify',        0,  4,  8, -1); // 5f 64×64  — Gorgon petrify hero overlay (looping)
  createAnim(scene, 'fx_aura_soul',      'effect_aura_soul',      0, 29, 18, -1); // 30f 128×128 — Necro soul aura (looping)
  // spell effects (all 72×72 frames)
  createAnim(scene, 'fx_spikes',         'effect_spikes',         0,  9, 14, 0);
  createAnim(scene, 'fx_explosion',      'effect_explosion',      0,  9, 14, 0);
  // Telurion: cyan ring/burst (single-target, 8 frames) and slate tornado
  // (AoE, 6 frames) — replaces the legacy storm-kite missile + verdant
  // green-burst. Both play once at the impact point.
  createAnim(scene, 'fx_telurion_st',    'effect_telurion_st',    0,  7, 16, 0);
  createAnim(scene, 'fx_telurion_aoe',   'effect_telurion_aoe',   0,  5, 12, 0);
  createAnim(scene, 'fx_lightning',      'effect_lightning',      0,  9, 16, 0);
  createAnim(scene, 'fx_lightning_bolt', 'effect_lightning_bolt', 0,  9, 16, 0);
  createAnim(scene, 'fx_midas',          'effect_midas',          0,  6, 12, 0);
  createAnim(scene, 'fx_sun_strike',     'effect_sun_strike',     0,  9, 14, 0);
  createAnim(scene, 'fx_fire_wall',      'effect_fire_wall',      0,  9, 14, 0);
  createAnim(scene, 'fx_black_hole',     'effect_black_hole',     0,  7, 10, 0);
  createAnim(scene, 'fx_fireball',       'effect_fireball',       0,  7, 14, 0);
  createAnim(scene, 'fx_poison_cloud',   'effect_poison_cloud',   0,  9, 12, 0);
  // Waterball — Zyphara frostbolt impact burst (2 frames)
  createAnim(scene, 'fx_blizzard',       'effect_blizzard',       0,  1, 10, 0);
  // Demon_1 boss spell VFX — laser beam (loops during channel) and charge-up orb (plays once)
  createAnim(scene, 'fx_demon_laser',   'effect_demon_laser',    0,  7, 16, -1); // looping beam
  createAnim(scene, 'fx_demon_charge',  'effect_demon_charge',   0,  5, 10,  0); // once — power buildup
  // Whirlwind red crescent sweep — Combat FX 1.1 row 25 (9 frames at offset 240)
  createAnim(scene, 'fx_whirlwind_slash', 'effect_combat_fx',    240, 248, 18, 0); // once, ~500ms
  // Combat FX row 28 — small spark/flash. Used as Cyrith's auto-attack
  // on-hit VFX (subtle so it doesn't spam the screen during fast swings).
  createAnim(scene, 'fx_cyrith_auto_strike', 'effect_combat_fx', 280, 289, 24, 0);
  // Thornlash claw slash — Combat FX 1.1 row 19 (frames 180-189). Played via dark-red tint.
  createAnim(scene, 'fx_thornlash_claw', 'effect_combat_fx',    180, 189, 18, 0);
  // Moonfire — Combat FX 1.1 gif 23 (frames 220-229). Played with a cool-blue
  // tint at the enemy's torso for the human-form Thornlash burst.
  createAnim(scene, 'fx_moonfire',       'effect_combat_fx',    220, 229, 18, 0);
  // Druid (Thorne) auto-attack swipe — Combat FX 1.1 row 12 (frames 110-119). Green nature swipe.
  createAnim(scene, 'fx_druid_autoswipe', 'effect_combat_fx',   110, 119, 18, 0);
  // Wild Growth magic burst (dedicated 576×72 8-frame sheet)
  createAnim(scene, 'fx_wildgrowth_magic', 'effect_wildgrowth_magic', 0, 7, 14, 0);
  // Void Lance projectile (boss_voidelf) — pack4/1 Magic/1, 20 frames at 72×72
  createAnim(scene, 'fx_void_lance',    'effect_void_lance',     0, 19, 14,  0); // once — lance in flight
  // Hellfire Ball projectile (boss_demon2) — pack4/1 Magic/8, 10 frames at 72×72
  createAnim(scene, 'fx_hellfire',      'effect_hellfire',       0,  9, 14,  0); // once — fire orb in flight
  createAnim(scene, 'fx_death_coil',   'effect_death_coil',     0,  7, 14,  0); // once — death coil green skull orb (8f 72×72)
  // fire magic effects — Lava Golem boss spells
  createAnim(scene, 'fx_fire_shield',   'effect_fire_shield',    0,  7, 10,  0); // once — Molten Shell activate (8f 128×128)
  createAnim(scene, 'fx_lava_drop',     'effect_lava_drop',      0, 15, 14,  0); // once — Magma Hurl projectile (16f 64×64)
  createAnim(scene, 'fx_fire_hurracane','effect_fire_hurracane', 0, 13, 12,  0); // once — Inferno Wave AoE (14f 256×256)
  // magic 7 — Ground Slam impact (8f 72×72)
  createAnim(scene, 'fx_ground_slam',   'effect_ground_slam',    0,  7, 12,  0); // once
  // shadow teleport — Wraith Strike (in = materialise, out = vanish; 10f 72×72)
  createAnim(scene, 'fx_shadow_teleport_in',  'effect_shadow_teleport_in',  0, 9, 14, 0); // once
  createAnim(scene, 'fx_shadow_teleport_out', 'effect_shadow_teleport_out', 0, 9, 14, 0); // once
  // Snowflake — Freezing Shot ground hit (13 frames 128×128)
  createAnim(scene, 'fx_snowflake', 'effect_snowflake', 0, 12, 14, 0); // once
  // Vine Snare VFX — Sylvari root burst (green burst sprite, 8 frames of 72×72)
  createAnim(scene, 'fx_vine_snare_hit', 'effect_vine_snare_hit', 0, 7, 10, 0); // once — root burst on enemy
  // Piercing Shot bolt — cyan magic projectile (8 frames of 72×72)
  createAnim(scene, 'fx_pierce_bolt', 'effect_pierce_bolt', 0, 7, 18, -1); // loop while traveling

  // ── Magic-spell-pack repurposed VFX (576×72, 8 frames each) ──────────────
  // fx_spell_pierce — Sylvari Piercing Shot replacement (blue lance trail).
  // Loops while traveling; first frame at the hero, last frame at the target.
  if (scene.textures.exists('effect_spell_pierce')) {
    createAnim(scene, 'fx_spell_pierce', 'effect_spell_pierce', 0, 7, 24, -1);
  }
  // fx_spell_verdant — Telurion Verdant Spike telegraph + impact burst (green
  // expanding magic circles). Plays once at the spike position, replacing
  // the placeholder add.circle telegraph + subsequent fx_spikes burst.
  if (scene.textures.exists('effect_spell_verdant')) {
    createAnim(scene, 'fx_spell_verdant', 'effect_spell_verdant', 0, 7, 12, 0);
  }
  // fx_spell_storm — Telurion Stormcall projectile + final crash. Plays once
  // at the strike point, replacing the bolt-on-impact only animation.
  if (scene.textures.exists('effect_spell_storm')) {
    createAnim(scene, 'fx_spell_storm', 'effect_spell_storm', 0, 7, 16, 0);
  }
  // fx_paralyzed — blue lightning crackle aura, looping. 7-frame status-pack
  // sprite. Used by Cyrith's Static Field to wrap Vorrak in an electric
  // shield (replaces the rotating dashed-ring graphic).
  if (scene.textures.exists('effect_paralyzed')) {
    createAnim(scene, 'fx_paralyzed', 'effect_paralyzed', 0, 6, 12, -1);
  }
  // Divine Wrath hit — blue flame burst (Combat FX sheet row 23 = frames 220–229, 64×64)
  createAnim(scene, 'fx_divine_wrath_hit', 'combat_fx_sheet', 220, 229, 10, 0); // play once on hit
}
